import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { NavLink, Link, useNavigate, useParams, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ViewerContext } from "@/components/Layout";
import { ProfileDialog } from "@/components/ProfileDialog";
import { MwlSymbol } from "@/components/MwlSymbol";
import { ThemeSelector } from "@/components/ThemeSelector";
import { useLoad, cad, emitRefresh, SectionBoundary } from "@/components/console/ui";
import { fmtDuration } from "@/components/SentenceClock";
import { Overview, ActivityFeed } from "@/components/console/Overview";
import { SentenceControl } from "@/components/console/SentenceControl";
import { Payments, Verify, Cashouts } from "@/components/console/Money";
import { Users, Asks, Contemplations } from "@/components/console/People";
import { Undertakings, Lists, Donations } from "@/components/console/Work";
import { Panel } from "@/components/console/ui";
import { XpQueue } from "@/components/console/XpQueue";

const GROUPS = [
  ["monitor", [["overview", "overview"], ["activity", "live activity"]]],
  ["act", [["verify", "undertaking proof"], ["xp", "xp verification"], ["cashouts", "cash outs"], ["sentence", "sentence & pot"]]],
  ["money", [["payments", "payments ledger"], ["donations", "foundation donations"]]],
  ["platform", [["users", "users"], ["asks", "asks & answers"], ["undertakings", "undertakings"], ["lists", "featured lists"], ["contemplations", "contemplations"]]],
];
const TITLES = Object.fromEntries(GROUPS.flatMap(([, items]) => items));

function Sidebar({ o }) {
  const badge = { verify: o?.review_queue, xp: o?.xp_pending, cashouts: o?.cashouts_pending };
  return (
    <aside className="hidden md:flex w-60 shrink-0 flex-col sticky top-0 h-screen" style={{ borderRight: "1px solid var(--border)", background: "var(--bg-secondary)" }} data-testid="console-sidebar">
      <Link to="/console/overview" className="flex items-center gap-3 px-5 h-16" style={{ borderBottom: "1px solid var(--border)" }} data-testid="console-home">
        <MwlSymbol size={30} strokeWidth={6} testId="console-symbol" />
        <span className="text-[11px] tracking-[0.3em] leading-tight">mawilu<br /><span style={{ color: "var(--accent)" }}>member console</span></span>
      </Link>
      <nav className="flex-1 overflow-y-auto py-4">
        {GROUPS.map(([g, items]) => (
          <div key={g} className="mb-5">
            <span className="block px-5 mb-1 text-[9px] tracking-[0.35em] opacity-50">{g}</span>
            {items.map(([k, label]) => (
              <NavLink key={k} to={`/console/${k}`} data-testid={`console-nav-${k}`}
                className="flex items-center justify-between px-5 py-2 text-[11px] tracking-[0.15em] transition-colors duration-150"
                style={({ isActive }) => ({ background: isActive ? "var(--accent)" : "transparent", color: isActive ? "#121212" : "var(--text-primary)" })}>
                <span>{label}</span>
                {badge[k] > 0 && <span className="text-[10px] px-[6px]" style={{ border: "1px solid currentColor" }} data-testid={`console-badge-${k}`}>{badge[k]}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="px-5 py-4" style={{ borderTop: "1px solid var(--border)" }}>
        <div className="[&>div]:mt-0 [&>div]:justify-start"><ThemeSelector inline /></div>
      </div>
    </aside>
  );
}

function TickClock({ s }) {
  const [at] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => { if (!s.active) return undefined; const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, [s.active]);
  const rem = s.active ? s.remaining_seconds - (now - at) / 1000 : s.remaining_seconds;
  return `${s.active ? "" : s.paused ? "paused " : "stopped "}${fmtDuration(rem)}`;
}

function TopBar({ title, o }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const s = o?.sentence;
  return (
    <header className="sticky top-0 z-40 h-16 flex items-center justify-between gap-4 px-6" style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-secondary)" }} data-testid="console-topbar">
      <h1 className="text-sm tracking-[0.3em]" data-testid="console-title"><span style={{ color: "var(--accent)" }}>/</span> {title}</h1>
      <div className="hidden lg:flex items-center gap-6 text-[10px] tracking-[0.2em]">
        <span data-testid="topbar-clock">clock · <span style={{ color: "var(--accent)" }}>{s ? <TickClock key={`${s.remaining_seconds}-${s.active}`} s={s} /> : "…"}</span></span>
        <span data-testid="topbar-pot">pot · <span style={{ color: "var(--accent)" }}>{cad(s?.pot_cad)}</span></span>
        <span className="flex items-center gap-2"><span className="w-2 h-2 animate-pulse" style={{ background: "var(--accent)" }} />live</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[10px] tracking-[0.2em] opacity-70 hidden sm:inline" data-testid="console-member-name">member · {user?.name}</span>
        <Link to="/home" data-testid="console-view-site" className="btn px-3 py-2 text-[10px] tracking-widest">view site</Link>
        <button onClick={() => { logout(); navigate("/login"); }} data-testid="console-exit" className="btn px-3 py-2 text-[10px] tracking-widest">exit</button>
      </div>
    </header>
  );
}

function MobileNav() {
  return (
    <nav className="md:hidden flex gap-2 overflow-x-auto px-4 py-3" style={{ borderBottom: "1px solid var(--border)" }} data-testid="console-mobile-nav">
      {Object.entries(TITLES).map(([k, l]) => (
        <NavLink key={k} to={`/console/${k}`} data-testid={`console-mobile-nav-${k}`} className="shrink-0 text-[10px] tracking-widest px-3 py-1" style={({ isActive }) => ({ border: "1px solid var(--border)", background: isActive ? "var(--accent)" : "transparent", color: isActive ? "#121212" : "var(--text-primary)" })}>{l}</NavLink>
      ))}
    </nav>
  );
}

export default function ConsolePage() {
  const { section = "overview" } = useParams();
  const navigate = useNavigate();
  const [o] = useLoad("/console/overview", 15000);
  const [viewUserId, setViewUserId] = useState(null);
  const viewProfile = useCallback((id) => setViewUserId(id), []);
  const viewer = useMemo(() => ({ viewProfile }), [viewProfile]);
  const go = (k) => navigate(`/console/${k}`);
  useEffect(() => {
    const id = api.interceptors.response.use((res) => {
      if (res.config.method !== "get") setTimeout(emitRefresh, 0);
      return res;
    });
    return () => api.interceptors.response.eject(id);
  }, []);
  if (!TITLES[section]) return <Navigate to="/console/overview" replace />;
  const body = {
    overview: <Overview go={go} />,
    activity: <Panel title="everything happening · chats & speak stay private" testId="console-activity"><ActivityFeed limit={200} /></Panel>,
    verify: <Verify />, xp: <XpQueue />, cashouts: <Cashouts />, sentence: <SentenceControl />, payments: <Payments />, donations: <Donations />,
    users: <Users />, asks: <Asks />, undertakings: <Undertakings />, lists: <Lists />, contemplations: <Contemplations />,
  }[section];
  return (
    <ViewerContext.Provider value={viewer}>
      <div className="min-h-screen flex console-grid" style={{ background: "var(--bg-primary)" }} data-testid="member-console">
        <Sidebar o={o} />
        <div className="flex-1 min-w-0 flex flex-col">
          <TopBar title={TITLES[section]} o={o} />
          <MobileNav />
          <main className="flex-1 p-4 md:p-8" key={section}><SectionBoundary>{body}</SectionBoundary></main>
        </div>
      </div>
      <ProfileDialog userId={viewUserId} noReview onClose={() => { setViewUserId(null); emitRefresh(); }} />
    </ViewerContext.Provider>
  );
}
